import React from 'react';
import {
    Button,
    FormGroup,
    Col,
    Row,
    Collapse,
    UncontrolledTooltip,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { orderBy, isEqual, isEmpty } from 'lodash-es';
import * as moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faWarehouse,
    faExclamationTriangle,
    faExclamation,
    faDownload,
    faPlus,
    faArrowUpRightFromSquare,
    faTasks,
} from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { Link } from '@s20/components/common/Link';
import { insertIfNotExists, upsert } from '@s20/common/util/array';
import { clone } from '@s20/common/util/object';
import { email as emailUtil, getZipPattern, patterns } from '@s20/common/util/validation';
import { httpClient } from '@s20/services/httpClient';
import Chip from '../common/forms/Chip';
import AddressInput from '../address/AddressInput';
import {
    AppPageForm,
    FlexCenterRow,
    FlexColumnCenter,
    FormCheckbox,
    FormDivider,
    onFieldChange,
    onReactSelectChanged,
    SimpleTable,
    SubHeading,
    ToastMessage,
    YesNoBadge,
    FormLabel,
    IconButton,
    Input,
    NumericInput,
    FormGroupColumn,
} from '../common/forms/FormElements';
import { BaseFormViewModel } from '../common/ViewModel';
import { Can } from '../Can';
import CommonContext, {
    ApiRoutes, AppNavPaths, ServiceResponseResult,
} from '../Common';
import CreateHolidayForm from '../holiday/CreateHolidayForm';
import BillingRateForm from '../billing/BillingRateForm';
import BillingRateHistoryForm from '../billing/BillingRateHistoryForm';
import { BillingRateGrid } from '../billing/BillingRateGrid';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { TipAlert } from '../common/TipAlert';
import { Block } from '../common/Block';
import { YesNoButtons } from '../common/forms/YesNoButtons';
import TimePicker from '../common/forms/TimePicker';
import EmailRecipients from '../common/forms/EmailRecipients';
import { SaveButton } from '../common/forms/SaveButton';
import { BillingRateTypes } from '../billing/BillingRate';
import { _ChargeTypeGroupStatuses } from '../chargeTypeGroup/ChargeTypeGroup';
import CompanyHistoryForm from './CompanyHistoryForm';
import OrganizationContactForm from './OrganizationContactForm';
import { MinimumWageRateGrid } from './MinimumWageRateGrid';
import MinimumWageRateForm from './MinimumWageRateForm';
import {
    OrganizationTypes,
    Organization,
    OrganizationContact,
    CompanyChargeTypeBillingRate,
    MinimumWageRate,
    BillingOTDays,
    CompanyChargeTypeGroupBillingRate,
    PayPeriodTypes,
} from './Organization';

const getDate = (dateInput, targetFormat = 'HH:mm') => {
    const formats = ['YYYY-MM-DD HH:mm A', 'L-d-YYYY HH:mm A', 'M/D/YYYY HH:mm A'];
    let startDate = '';

    formats.forEach((format) => {
        const date = moment(dateInput, format);

        if (date.isValid()) {
            startDate = date.format(targetFormat);
        }
    });

    return startDate;
};

class OrganizationForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.contactFormRef = React.createRef();
        this.rateFormRef = React.createRef();
        this.minWageRateFormRef = React.createRef();
        this.rateHistoryFormRef = React.createRef();
        this.billingRatesRef = React.createRef();

        const stateBase = {
            contactsFormValidated: false,
            customers: [],
            invoiceTerms: [],
            invoiceMappings: [],
            isCustomer: false,
            isBilling: false,
            isDispatch: false,
            isPayroll: false,
            isOperationsCenter: false,
            isSubcontractor: false,
            isDispatchPort: false,
            dispatchPortOptions: [],
            isParentCustomer: false,
            isProspect: false,
            isVBOffice: false,
            organization: new Organization(),
            types: [],
            selectedContact: null,
            showContactsForm: false,
            showCompanyHistory: false,
            parentCompanies: [],
            subcontractorCustomersOptions: [],
            contactFormIsReadOnly: false,
            chargeTypeGroups: [],
            selectedMinWageRate: null,
            showMinWageRateForm: false,
            showMinWageRateFormDelete: true,
            useInvoicingRequired: false,
            zipPattern: '',
            payPeriodTypes: [],
            defineCustomHolidays: false,
            holidays: [],
            selectedHolidays: [],
            currentDailyReportEmailList: '',
            currentHandoffEmailRecipients: '',
            currentNotificationEmailRecipients: '',
            billingGroup: null,
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.updateOrganization = this.updateOrganization.bind(this);
        this.onBillingCountryChanged = this.onBillingCountryChanged.bind(this);
        this.onCountryChange = this.onCountryChange.bind(this);
    }

    // #region INIT
    componentDidMount() {
        this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps
            && this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }

        if (
            prevProps.location.hash == '#billingRates'
            && this.billingRatesRef.current != undefined
        ) {
            this.billingRatesRef.current.scrollIntoView();
        }
    }

    onDefineCustomHolidaysChanged = (e) => {
        const { checked } = e.target;

        this.setState((prev) => ({
            defineCustomHolidays: checked,
            selectedHolidays: checked ? prev.selectedHolidays : [],
            organization: {
                ...prev.organization,
                hasCustomHolidayList: checked,
            },
        }));
    };


    onAddContact = () => {
        this.context.setFormOpened(true);
        this.contactFormRef.current.resetForm();
        this.setState({
            selectedContact: /* createFakeContact() */ new OrganizationContact(),
            showContactsForm: true,
            allowContactTypeEdit: true,
        });
    };

    onAddMinWageRate = () => {
        const new_rate = new MinimumWageRate();

        this.setState({
            selectedMinWageRate: new_rate,
            showMinWageRateForm: true,
            minWageRateFormValidated: false,
            showMinWageRateFormDelete: false,
        });
        this.context.setFormOpened(true);
    };

    onAddRate = () => {
        this.rateFormRef.current.resetForm(); // clear validation
        this.setState((state) => {
            state.selectedRate = { ...new CompanyChargeTypeBillingRate() };
            state.showRateForm = true;
            return state;
        });
        this.context.setFormOpened(true); // lock all other page fields while form is open
    };

    onAddGroupRate = () => {
        this.rateFormRef.current.resetForm(); // clear validation
        this.setState((state) => {
            state.selectedRate = { ...new CompanyChargeTypeGroupBillingRate() };
            state.showRateForm = true;
            return state;
        });
        this.context.setFormOpened(true); // lock all other page fields while form is open
    };

    onChange = onFieldChange;

    // #endregion

    onClearErrors = () => this.setState({ errors: {} });

    onClose = (response) => {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    };

    onDelete = async () => {
        const { organization } = this.state;
        const response = await httpClient
            .delete(ApiRoutes.company.delete(organization.id))
            .catch(this.onCatchFetchError);
        if (response) this.onClose(response);
    };

    onEditContact = (contact) => {
        this.contactFormRef.current.resetForm();

        // Only allow contact type edit if it's a new contact that's being edited before being saved.
        // Can't edit contact types for an already saved contact, in other words.
        const allowCTEdit = !contact.id;

        this.setState({
            selectedContact: contact,
            showContactsForm: true,
            allowContactTypeEdit: allowCTEdit,
        });
    };

    onAddPreferredPersonnel = (pe) => {
        const { selectedContact } = this.state;
        if (!(selectedContact.preferredEmployees ?? []).some((x) => x.employeeId === pe.employeeId)) {
            selectedContact.preferredEmployees = [...(selectedContact.preferredEmployees ?? []), pe];
            this.setState({ selectedContact });
        }
    };

    onRemovePreferredPersonnel = (employeeId) => {
        const { selectedContact } = this.state;
        selectedContact.preferredEmployees = (selectedContact.preferredEmployees ?? []).filter(
            (x) => x.employeeId !== employeeId,
        );
        this.setState({ selectedContact });
    };

    onEditMinWageRate = (rate) => {
        this.minWageRateFormRef.current.resetForm();
        this.context.setFormOpened(true);
        this.setState({
            selectedMinWageRate: clone(rate),
            showMinWageRateForm: true,
            minWageRateFormValidated: false,
            showMinWageRateFormDelete: true,
        });
        this.context.setFormOpened(true);
    };

    onEditRate = (rate) => {
        this.rateFormRef.current.resetForm();
        this.setState({
            selectedRate: { ...rate, rateType: BillingRateTypes.CHARGE_TYPE },
            showRateForm: true,
            showRateFormDelete: true,
        });
        this.context.setFormOpened(true);
    };

    onEditGroupRate = (rate) => {
        this.rateFormRef.current.resetForm();
        this.setState({
            selectedRate: { ...rate, rateType: BillingRateTypes.CHARGE_TYPE_GROUP },
            showRateForm: true,
            showRateFormDelete: true,
        });
        this.context.setFormOpened(true);
    };

    onTimeChanged = (ev) => {
        const { organization } = this.state;

        const { value, name } = ev.target;

        const timeValue = value
            ? moment(value, ['h:mm A']).format('HH:mm')
            : '';

        organization[name] = timeValue;
        this.setState({ organization });
    };

    onEmailAdded = () => {
        const { currentDailyReportEmailList, organization } = this.state;
        if (currentDailyReportEmailList) {
            const emailAddress = currentDailyReportEmailList.trim();
            const match = emailUtil(emailAddress);
            const value = organization.dailyReportEmailList;

            if (match) {
                const newValue = value?.length ? value.split(',') : [];
                if (!newValue.includes(emailAddress)) {
                    newValue.push(emailAddress);
                }
                organization.dailyReportEmailList = newValue.join(',');
                this.setState({
                    organization,
                    currentDailyReportEmailList: '',
                });
            } else {
                toast.error('Please enter a single valid email address.');
            }
        }
    };

    onEmailChanged = (e) => {
        const val = e.currentTarget.value;
        this.setState({ currentDailyReportEmailList: val });
    };

    onEmailRemoved = (selected) => {
        const { organization } = this.state;
        const value = organization.dailyReportEmailList;
        const valueArr = value.split(',');
        const index = valueArr.findIndex(
            (ea) => ea === selected,
        );
        valueArr.splice(index, 1);
        organization.dailyReportEmailList = valueArr.join(',');
        this.setState({ organization });
    };

    onHandoffEmailAdded = () => {
        const { currentHandoffEmailRecipients, organization } = this.state;
        if (currentHandoffEmailRecipients) {
            const emailAddress = currentHandoffEmailRecipients.trim();
            const match = emailUtil(emailAddress);
            const value = organization.handoffEmailRecipients;

            if (match) {
                const newValue = value?.length ? value.split(',') : [];
                if (!newValue.includes(emailAddress)) {
                    newValue.push(emailAddress);
                }
                organization.handoffEmailRecipients = newValue.join(',');
                this.setState({
                    organization,
                    currentHandoffEmailRecipients: '',
                });
            } else {
                toast.error('Please enter a single valid email address.');
            }
        }
    };

    onHandoffEmailChanged = (e) => {
        const val = e.currentTarget.value;
        this.setState({ currentHandoffEmailRecipients: val });
    };

    onHandoffEmailRemoved = (selected) => {
        const { organization } = this.state;
        const value = organization.handoffEmailRecipients;
        const valueArr = value.split(',');
        const index = valueArr.findIndex(
            (ea) => ea === selected,
        );
        valueArr.splice(index, 1);
        organization.handoffEmailRecipients = valueArr.join(',');
        this.setState({ organization });
    };

    onNotificationEmailAdded = () => {
        const { currentNotificationEmailRecipients, organization } = this.state;
        if (currentNotificationEmailRecipients) {
            const emailAddress = currentNotificationEmailRecipients.trim();
            const match = emailUtil(emailAddress);
            const value = organization.notificationEmailRecipients;

            if (match) {
                const newValue = value?.length ? value.split(',') : [];
                if (!newValue.includes(emailAddress)) {
                    newValue.push(emailAddress);
                }
                organization.notificationEmailRecipients = newValue.join(',');
                this.setState({
                    organization,
                    currentNotificationEmailRecipients: '',
                });
            } else {
                toast.error('Please enter a single valid email address.');
            }
        }
    };

    onNotificationEmailChanged = (e) => {
        const val = e.currentTarget.value;
        this.setState({ currentNotificationEmailRecipients: val });
    };

    onNotificationEmailRemoved = (selected) => {
        const { organization } = this.state;
        const value = organization.notificationEmailRecipients;
        const valueArr = value.split(',');
        const index = valueArr.findIndex(
            (ea) => ea === selected,
        );
        valueArr.splice(index, 1);
        organization.notificationEmailRecipients = valueArr.join(',');
        this.setState({ organization });
    };

    onMinWageRateFormClosed = () => {
        this.minWageRateFormRef.current.resetForm(); // clear validation
        this.context.setFormOpened(false); // lock all other page fields while form is open
        this.setState({
            showMinWageRateForm: false,
            showMinWageRateFormDelete: false,
        });
    };

    onMinWageRateRemoved = () => {
        const { selectedMinWageRate, organization } = this.state;

        organization.companyMinimumWageRates = organization.companyMinimumWageRates.filter(
            (x) => x.id !== selectedMinWageRate.id,
        );
        this.rateFormRef.current.resetForm();
        this.setState({
            organization,
            showMinWageRateForm: false,
        });
    };

    onMinWageRateSaved = () => {
        const { selectedMinWageRate, organization } = this.state;

        const existing = organization.companyMinimumWageRates.find(
            (x) => x.id === selectedMinWageRate.id,
        );

        // need to expire any or look for overlap
        // There should be only 1 current per charge type
        const previousRate = organization.companyMinimumWageRates.find(
            (x) => !x.endDate || x.endDate >= selectedMinWageRate.startDate,
        );
        if (previousRate) {
            // set the expiration date
            const effective = new Date(
                clone(selectedMinWageRate.startDate),
            );

            // see if any have same effective date
            if (selectedMinWageRate.id === 0) {
                if (
                    new Date(previousRate.startDate).getDate()
                    == effective.getDate()
                    && previousRate.id !== selectedMinWageRate.id
                ) {
                    // pop notification?
                    this.minWageRateFormRef.current.resetForm();
                    this.setState({
                        showMinRateRateForm: false,
                    });
                    return;
                }
                // set it back one day.
                effective.setDate(effective.getDate() - 1);
                previousRate.endDate = effective;
            }
        }

        if (existing) {
            organization.companyMinimumWageRates[
                organization.companyMinimumWageRates.indexOf(existing)
            ] = selectedMinWageRate;
        } else {
            organization.companyMinimumWageRates.push(
                clone(selectedMinWageRate),
            );
        }

        this.setState({
            organization,
            showMinWageRateForm: false,
            selectedMinWageRate: null,
            minWageRateFormValidated: false,
        });

        this.context.setFormOpened(false);
    };

    onOTDaysChanged = (event, otDay) => {
        const { organization } = { ...this.state };
        organization.billingOTDays ^= otDay;
        this.setState({ organization: { ...organization } });
    };

    onOrgTypeChanged = async () => {
        const { organization } = { ...this.state };

        const isBilling = (organization.types ?? []).includes(
            OrganizationTypes.Billing,
        );
        const isPayroll = (organization.types ?? []).includes(
            OrganizationTypes.Payroll,
        );
        const isDispatch = (organization.types ?? []).includes(
            OrganizationTypes.Dispatch,
        );
        const isCustomer = (organization.types ?? []).includes(
            OrganizationTypes.Customer,
        );
        const isOperationsCenter = (organization.types ?? []).includes(
            OrganizationTypes.OperationsCenter,
        );
        const isSubcontractor = (organization.types ?? []).includes(
            OrganizationTypes.CustomerSubcontractor,
        );
        const isParentCustomer = (organization.types ?? []).includes(
            OrganizationTypes.ParentCustomer,
        );
        const isProspect = (organization.types ?? []).includes(
            OrganizationTypes.Prospect,
        );
        const isVBOffice = (organization.types ?? []).includes(
            OrganizationTypes.VendorBillOffice,
        );
        const isDispatchPort = (organization.types ?? []).includes(
            OrganizationTypes.DispatchPort,
        );
        let customers = [];
        let parentRequired = false;
        let parentCustomers = [];
        let parentCompanies = [];
        let parentCompanyLabelText = '';

        if (isBilling) {
            // do nothing.  this is top tier hierarchy
        } else if (isPayroll && !isBilling) {
            // if not a billing type, the billing company must be provided.
            parentCompanies = await httpClient.js(
                ApiRoutes.typeAheads.billingCompanies(),
            );
            parentCompanyLabelText = 'Billing Company';
            parentRequired = true;
        } else if (isDispatch && !isPayroll && !isBilling) {
            // If not a payroll type and a dispatch location, need the payroll company.
            parentCompanies = await httpClient.js(
                ApiRoutes.typeAheads.payrollCompanies(),
            );
            parentCompanyLabelText = 'Payroll Company';
            parentRequired = true;
        } else if (isDispatchPort) {
            parentCompanies = await httpClient.js(
                ApiRoutes.typeAheads.dispatchLocations(),
            );
            parentCompanyLabelText = 'Dispatch Office';
            parentRequired = true;
        }

        let useInvoicingRequired = false;
        if (isOperationsCenter) {
            customers = await httpClient.js(ApiRoutes.typeAheads.customers());
            // Remove any self-reference as a protection.
            customers = (customers ?? []).filter((x) => x.value !== organization.id)
                ?? [];

            if (organization.customerCompanyId !== null) {
                const opCenterParent = await httpClient.js(
                    ApiRoutes.company.byId(organization.customerCompanyId),
                );
                useInvoicingRequired = opCenterParent
                    && opCenterParent.operationsCenterUseInvoicing;
            }
        }

        if (!isParentCustomer) {
            parentCustomers = await httpClient.js(
                ApiRoutes.typeAheads.parentCustomers(),
            );
        }

        // Remove self-reference as a protection.
        parentCompanies = (parentCompanies ?? []).filter(
            (x) => x.value !== organization.id,
        ) ?? [];

        if (isBilling) organization.parentCompanyId = null;

        // If this org is not a contractor, clear out customers array.
        if (!isSubcontractor) organization.subcontractorCustomers = [];

        if (!organization.id && isCustomer) {
            const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};
            if (tenantSettings.invoicingEnabled) organization.useInvoicing = true;
        }

        let dispatchPortOptions = [];
        if (isDispatch && organization.id) {
            const ports = await httpClient.js(
                ApiRoutes.typeAheads.dispatchPorts(organization.id),
            );
            dispatchPortOptions = [
                { label: '— None (use office location) —', value: null },
                ...(ports ?? []).map((p) => ({ label: p.label, value: p.value })),
            ];
        }

        this.setState({
            customers: [...customers],
            isOperationsCenter,
            isCustomer,
            isDispatch,
            isBilling,
            isSubcontractor,
            isDispatchPort,
            dispatchPortOptions,
            isParentCustomer,
            isPayroll,
            isProspect,
            isVBOffice,
            parentCompanyLabelText,
            parentCompanies: [...parentCompanies],
            parentCustomers: [...parentCustomers],
            organization: { ...organization },
            parentRequired,
            useInvoicingRequired,
        });
    };

    onDefaultDispatchPortChanged = (selected) => {
        const { organization } = this.state;
        organization.defaultDispatchPortId = selected?.value ?? null;
        this.setState({ organization });
    };

    onOrgTypeSelectChanged = (item) => {
        const { organization } = { ...this.state };
        organization.types = (item ?? []).map((x) => x.value) ?? [];
        this.setState({ organization });
        this.onOrgTypeChanged();
    };

    onStateChange = (item) => {
        const { organization } = { ...this.state };
        organization.stateId = item?.id || '';
        organization.state = item?.value || '';
        if (!organization.stateId) {
            organization.state = '';
        }
        this.setState({ organization });
    };

    onOrganizationContactSaved = () => {
        this.setState({ contactFormIsReadOnly: true });
        const { selectedContact, organization } = this.state;

        organization.contacts = insertIfNotExists(
            organization.contacts,
            selectedContact,
        );
        this.contactFormRef.current.resetForm();
        this.setState({
            organization,
            showContactsForm: false,
            contactFormIsReadOnly: false,
        });
    };

    onPhoneChanged = (event) => {
        const { value } = event.target;
        const { organization } = { ...this.state };
        organization.phone = value;
        this.setState({ organization: { ...organization } });
    };

    onRateFormClosed = () => {
        this.rateFormRef.current.resetForm(); // clear validation
        this.context.setFormOpened(false); // lock all other page fields while form is open
        this.setState({
            showRateForm: false,
            showRateFormDelete: false,
            selectedRate: null,
        });
    };

    onRateHistoryFormClosed = () => {
        this.rateFormRef.current.resetForm(); // clear validation
        this.context.setFormOpened(false); // lock all other page fields while form is open
        this.setState({
            showRateHistoryForm: false,
        });
    };

    onRateRemoved = () => {
        const { selectedRate, organization } = this.state;

        if (selectedRate.rateType === BillingRateTypes.CHARGE_TYPE) {
            organization.billingRates = organization.billingRates.filter(
                (x) => x.billingRateId !== selectedRate.billingRateId,
            );
        } else {
            organization.groupBillingRates = organization.groupBillingRates.filter(
                (x) => x.billingRateId !== selectedRate.billingRateId,
            );
        }

        this.rateFormRef.current.resetForm();
        this.setState({
            organization,
            showRateForm: false,
            selectedRate: null,
        });
    };

    onRateSaved = () => {
        const {
            selectedRate, organization, chargeTypes, chargeTypeGroups,
        } = this.state;

        if (selectedRate.rateType === BillingRateTypes.CHARGE_TYPE) {
            // need to update the charge type name
            selectedRate.chargeTypeName = (chargeTypes ?? []).find(
                (x) => x.value === selectedRate.chargeTypeId,
            ).label;
            // need to expire any or look for overlap
            // There should be only 1 current per charge type
            const rateWithChargeType = organization.billingRates.find(
                (x) => x.chargeTypeId == selectedRate.chargeTypeId
                    && (!x.billingRate.expirationDate
                        || x.billingRate.expirationDate
                        >= selectedRate.billingRate.effectiveDate),
            );
            if (rateWithChargeType) {
                // set the expiration date
                const effective = new Date(
                    clone(selectedRate.billingRate.effectiveDate),
                );

                // see if any have same effective date
                if (typeof selectedRate.billingRateId === 'string') {
                    if (
                        new Date(
                            rateWithChargeType.billingRate.effectiveDate,
                        ).getDate() == effective.getDate()
                        && rateWithChargeType.billingRateId
                        !== selectedRate.billingRateId
                    ) {
                        // pop notification?
                        this.rateFormRef.current.resetForm();
                        this.setState({
                            showRateForm: false,
                        });
                        return;
                    }
                    // set it back one day.
                    effective.setDate(effective.getDate() - 1);
                    rateWithChargeType.billingRate.expirationDate = effective;
                }
            }

            organization.billingRates = upsert(
                selectedRate,
                organization.billingRates ?? [],
                'billingRateId',
            );
        } else {
            // need to update the charge type name
            selectedRate.chargeTypeName = (chargeTypeGroups ?? []).find(
                (x) => x.value === selectedRate.chargeTypeGroupId,
            ).label;
            // need to expire any or look for overlap
            // There should be only 1 current per charge type
            const rateWithChargeTypeGroup = organization.groupBillingRates.find(
                (x) => x.chargeTypeId == selectedRate.chargeTypeId
                    && (!x.billingRate.expirationDate
                        || x.billingRate.expirationDate
                        >= selectedRate.billingRate.effectiveDate),
            );
            if (rateWithChargeTypeGroup) {
                // set the expiration date
                const effective = new Date(
                    clone(selectedRate.billingRate.effectiveDate),
                );

                // see if any have same effective date
                if (typeof selectedRate.billingRateId === 'string') {
                    if (
                        new Date(
                            rateWithChargeTypeGroup.billingRate.effectiveDate,
                        ).getDate() == effective.getDate()
                        && rateWithChargeTypeGroup.billingRateId !== selectedRate.billingRateId
                    ) {
                        // pop notification?
                        this.rateFormRef.current.resetForm();
                        this.setState({
                            showRateForm: false,
                        });
                        return;
                    }
                    // set it back one day.
                    effective.setDate(effective.getDate() - 1);
                    rateWithChargeTypeGroup.billingRate.expirationDate = effective;
                }
            }

            organization.groupBillingRates = upsert(
                selectedRate,
                organization.groupBillingRates ?? [],
                'billingRateId',
            );
        }

        this.rateFormRef.current.resetForm();
        this.setState({
            organization,
            showRateForm: false,
            selectedRate: null,
        });
    };

    onSelectChanged = onReactSelectChanged;

    onShowRateHistory = async (rate) => {
        const { organization } = { ...this.state };

        const model = {
            companyId: organization.id,
            chargeTypeId: rate.chargeTypeId,
        };
        const history = await httpClient.post(
            ApiRoutes.billingRate.companyBillingRateHistory(),
            model,
        );

        this.rateHistoryFormRef.current.resetForm();
        this.setState({
            selectedRateHistory: history,
            showRateHistoryForm: true,
        });
        this.context.setFormOpened(true); // lock all other page fields while form is open
    };

    // #endregion

    // #region METHODS
    onSubmit = async () => {
        // Clear any fluent api errors
        this.onClearErrors();
        this.setSaving(true);

        const { organization } = { ...this.state };

        /** Revert temporary hashes used in row keys.  Need this to get around
            fluent API.
        */
        for (const contact of organization.contacts) contact.id = typeof contact.id === 'string' ? null : contact.id;

        for (const rate of organization.billingRates) {
            rate.billingRateId = typeof rate.billingRateId === 'string'
                ? null
                : rate.billingRateId;

            rate.billingRate.id = typeof rate.billingRate.id === 'string'
                ? null
                : rate.billingRate.id;
        }

        for (const rate of organization.groupBillingRates) {
            rate.billingRateId = typeof rate.billingRateId === 'string'
                ? null
                : rate.billingRateId;

            rate.billingRate.id = typeof rate.billingRate.id === 'string'
                ? null
                : rate.billingRate.id;
        }

        if (this.props.location.pathname === AppNavPaths.OrganizationNew) {
            await this.createOrganization(organization);
        } else {
            await this.updateOrganization(organization);
        }
    };

    setSaving = (b) => this.setState({ saving: b });

    canEditCodes = () => {
        if (!this.context || !this.context.checkUserHasPermission) {
            return false;
        }

        return this.context.checkUserHasPermission('company_code.edit');
    };

    createOrganization = async (organization) => {
        if (organization.useContractSchedule) {
            const clearedSettings = {
                billingUsesOT: false,
                billingUseDailyOT: false,
                billingOTShiftStart: '',
                billingOTShiftEnd: '',
                billingOTJobStart: '',
                billingOTJobEnd: '',
                billingDailyOTThreshold: 0,
            };

            organization = { ...organization, ...clearedSettings };
        }

        const response = await httpClient
            .post(ApiRoutes.company.create(), organization)
            .catch(this.handleSaveError);
        if (response) {
            if (response.result === ServiceResponseResult.Ok) {
                await this.saveHolidays(response.data);
                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Save Successful"
                        message="Saved."
                    />,
                );

                this.context.history.push(
                    `${AppNavPaths.Organization}/${response.data}`,
                );
            } else {
                toast.error(
                    <ToastMessage
                        icon={faExclamationTriangle}
                        header="There was a problem saving"
                        message={`${response.message}`}
                    />,
                );
            }
        }
        this.setSaving(false);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    getOrganizationId = () => this.props.match.params.id;

    onAddHoliday = () => {
        this.setState({ showAddHolidayForm: true });
    };

    onCloseHolidayForm = () => {
        this.setState({ showAddHolidayForm: false });
    };

    refreshHolidays = async () => {
        const updatedHolidays = await httpClient.js(ApiRoutes.holiday.all());
        this.setState({ holidays: updatedHolidays });
    };

    onHolidayCreated = (newHolidayOption) => {
        this.setState((prevState) => {
            const updatedHolidays = [...prevState.holidays, newHolidayOption];
            const updatedSelected = [...prevState.selectedHolidays, newHolidayOption];

            return {
                holidays: updatedHolidays,
                selectedHolidays: updatedSelected,
            };
        });
    };

    async saveHolidays(companyId) {
        const holidayIds = this.state.selectedHolidays.map((h) => h.value);

        try {
            if (!isEmpty(holidayIds)) {
                await httpClient.post(
                    ApiRoutes.holiday.companyHolidays(companyId),
                    holidayIds,
                );
            }
        } catch (error) {
            console.error(error);
            toast.error('Error defining company holidays');
        }
    }


    async populateState() {
        const orgId = this.getOrganizationId();

        const [
            companyTypes,
            companyStatuses,
            organization,
            allCustomers,
            chargeTypes,
            invoiceTerms,
            invoiceMappings,
            chargeTypeGroups,
            payPeriodTypes,
        ] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.companyTypes()),
            httpClient.js(ApiRoutes.typeAheads.companyStatuses()),
            orgId
                ? httpClient.js(ApiRoutes.company.byId(orgId))
                : new Organization(),
            httpClient.js(ApiRoutes.typeAheads.subcontractingCustomers()),
            httpClient.js(ApiRoutes.typeAheads.chargeTypesBillable()),
            httpClient.js(ApiRoutes.typeAheads.invoiceTerms()),
            httpClient.js(ApiRoutes.typeAheads.invoiceMapping()),
            httpClient.js(ApiRoutes.typeAheads.chargeTypeGroups()),
            httpClient.js(ApiRoutes.typeAheads.payPeriodTypes()),
        ]);

        //Fetch holidays
        const allHolidaysResp = await httpClient.js(ApiRoutes.holiday.all());
        const allHolidays = allHolidaysResp;

        let currentHolidayIds = [];
        if (orgId) {
            currentHolidayIds = await httpClient.js(ApiRoutes.holiday.companyHolidays(orgId));
        }

        const selectedHolidays = allHolidays.filter((opt) =>
            currentHolidayIds.includes(opt.value),
        );

        const defineCustomHolidaysDefault = organization.hasCustomHolidayList
            || selectedHolidays.length > 0;

        const [countries] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.countries()),
        ]);

        const tenantCountryId = this.context?.tenant?.countryId ?? null;

        // Billing Country
        if (!organization.billingCountryId && tenantCountryId) {
            organization.billingCountryId = tenantCountryId;

            const tenantCountryOpt = (countries || []).find(
                (c) => c.id === tenantCountryId || String(c.value) === String(tenantCountryId),
            );

            organization.billingCountryCode = tenantCountryOpt?.value || '';

            const usStatesFromTenant =
                await httpClient.js(ApiRoutes.typeAheads.USStatesbyCountryId(tenantCountryId));

            this.setState({
                usStates: usStatesFromTenant || [],
                zipPattern: getZipPattern(tenantCountryId),
            });
        } else if (organization.billingCountryId) {
            const usStates = await httpClient.js(
                ApiRoutes.typeAheads.USStatesbyCountryId(organization.billingCountryId),
            );

            this.setState({ usStates: usStates || [], zipPattern: getZipPattern(organization.billingCountryId) });
        }

        // Organization Country
        if (!organization.countryId && tenantCountryId) {
            organization.countryId = tenantCountryId;
            organization.countryCode = (countries || []).find(
                (c) => c.id === tenantCountryId || String(c.value) === String(tenantCountryId),
            )?.value || '';
            const usStates = await httpClient.js(ApiRoutes.typeAheads.USStatesbyCountryId(tenantCountryId));
            this.setState({ usStates: usStates || [], zipPattern: getZipPattern(tenantCountryId) });
        }

        organization.billingOTShiftStart = organization.billingOTShiftStart
            ? getDate(organization.billingOTShiftStart)
            : '';
        organization.billingOTShiftEnd = organization.billingOTShiftEnd
            ? getDate(organization.billingOTShiftEnd)
            : '';

        organization.billingOTJobStart = organization.billingOTJobStart
            ? getDate(organization.billingOTJobStart)
            : '';
        organization.billingOTJobEnd = organization.billingOTJobEnd
            ? getDate(organization.billingOTJobEnd)
            : '';

        organization.customShiftDifferentialStart = organization.customShiftDifferentialStart
            ? getDate(organization.customShiftDifferentialStart)
            : '';
        organization.customShiftDifferentialEnd = organization.customShiftDifferentialEnd
            ? getDate(organization.customShiftDifferentialEnd)
            : '';

        const originalData = clone(organization);

        this.setState(
            {
                originalData,
                organization,
                loading: false,
                companyTypes,
                companyStatuses,
                saving: false,
                subcontractorCustomersOptions: allCustomers,
                chargeTypes,
                invoiceTerms,
                invoiceMappings,
                chargeTypeGroups,
                allowContactTypeEdit: false,
                countries,
                payPeriodTypes,
                holidays: allHolidays,
                selectedHolidays,
                defineCustomHolidays: defineCustomHolidaysDefault,
                useInvoicingRequired: false,
            },
            async () => {
                await this.onOrgTypeChanged();
            },
        );
    }

    resetForm = () => this.setState({ formValidated: false });

    subcontractorCustomersChanged = (selection) => {
        const value = selection ? [...selection] : [];
        const customers = value.map((x) => x.value) ?? [];
        this.setState(
            (state) => (
                (state.organization.subcontractorCustomers = customers), state
            ),
        );
    };

    updateOrganization = async (organization) => {
        if (organization.useContractSchedule) {
            const clearedSettings = {
                billingUsesOT: false,
                billingUseDailyOT: false,
                billingOTShiftStart: '',
                billingOTShiftEnd: '',
                billingOTJobStart: '',
                billingOTJobEnd: '',
                billingDailyOTThreshold: 0,
            };

            organization = { ...organization, ...clearedSettings };
        }
        const response = await httpClient
            .put(ApiRoutes.company.byId(organization.id), organization)
            .catch(this.handleSaveError);
        if (response) {
            response.billingOTShiftStart = organization.billingOTShiftStart
                ? getDate(response.billingOTShiftStart)
                : '';

            response.billingOTShiftEnd = organization.billingOTShiftEnd
                ? getDate(response.billingOTShiftEnd)
                : '';

            response.billingOTJobStart = organization.billingOTJobStart
                ? getDate(response.billingOTJobStart)
                : '';
            response.billingOTJobEnd = organization.billingOTJobEnd
                ? getDate(response.billingOTJobEnd)
                : '';

            response.customShiftDifferentialStart = organization.customShiftDifferentialStart
                ? getDate(response.customShiftDifferentialStart)
                : '';
            response.customShiftDifferentialEnd = organization.customShiftDifferentialEnd
                ? getDate(response.customShiftDifferentialEnd)
                : '';


            if (response && parseInt(response.id ?? 0) >= 0) {
                //save holiday
                await this.saveHolidays(response.id);
                this.setState({
                    organization: response,
                    originalData: clone(response),
                    saving: false,
                    formValidated: false,
                });

                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Save Successful"
                        message={`[${response.name}] saved.`}
                    />,
                );
            }
        }

        this.setSaving(false);
    };

    renderContacts = () => {
        const { contacts } = this.state.organization;
        if (!contacts) {
            return '';
        }

        return contacts.map((contact, i) => {
            let primaryPhone = '';

            if (contact.officePhoneIsPrimary) primaryPhone = contact.officePhone ?? contact.mobilePhone;
            else primaryPhone = contact.mobilePhone ?? contact.officePhone;

            return (
                <tr key={i}>
                    <td>{`${contact.lastName}, ${contact.firstName}`}</td>
                    <td>{contact.title}</td>
                    <td>
                        <a href={`mailto: ${contact.email}`}>{contact.email}</a>
                    </td>
                    <td>{primaryPhone}</td>
                    <td>{contact.locationContactTypeName}</td>
                    <td>{contact.isActive ? 'Yes' : 'No'}</td>
                    <Can
                        I="edit"
                        a="company"
                    >
                        <CommonContext.Consumer>
                            {(value) => (
                                <td>
                                    <Button
                                        disabled={
                                            !!value.formIsOpen
                                            || this.state.organization
                                                .companyStatusId != 1
                                        }
                                        type="button"
                                        color="outline-primary"
                                        onClick={() => this.onEditContact(contact)}
                                        size="sm"
                                    >
                                        Edit
                                    </Button>
                                </td>
                            )}
                        </CommonContext.Consumer>
                    </Can>
                </tr>
            );
        });
    };

    renderJobRows = () => {
        const { jobs } = this.state.organization;
        if (!jobs) return '';
        return jobs.map((job, i) => (
            <tr key={i}>
                <td>{job.id}</td>
                <td>{job.name}</td>
                <td>
                    <YesNoBadge value={job.active} />
                </td>
            </tr>
        ));
    };

    onCountryChange(selection) {
        const { organization } = this.state;
        organization.countryId = selection?.id;
        organization.countryCode = selection?.value;
        if (!organization.countryId) {
            organization.stateId = '';
            organization.country = '';
            organization.state = '';
        }
        this.setState({ organization });
    }

    async onBillingCountryChanged(selection) {
        const { organization } = this.state;
        const country = selection?.id || '';

        organization.billingCountryId = country;
        organization.billingCountryCode = selection?.value || '';

        this.setState({ organization, usStates: [], zipPattern: '' });

        if (country) {
            const usStates = await httpClient.js(
                ApiRoutes.typeAheads.USStatesbyCountryId(country),
            );

            this.setState({ zipPattern: getZipPattern(country), usStates: usStates || [] });
        }
    }

    downloadDailyOperationalReport = async (e) => {
        e.preventDefault();

        try {
            const { id } = this.context.tenant;
            const organizationId = this.state.organization.id;
            const url = ApiRoutes.report.downloadDailyOperationalReport(id, organizationId);

            await httpClient.post(url);

            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Success"
                    message="A daily operational report is being generated and will be emailed to you."
                />,
            );
        } catch {
            toast.error(
                <ToastMessage
                    icon={faExclamation}
                    header="Error"
                    message="An error occurred while downloading the daily operational report."
                />,
            );
        }
    };

    onBillingStateChanged = (item) => {
        const { organization } = { ...this.state };
        organization.billingStateId = item?.id || '';
        organization.billingState = item?.value || '';
        this.setState({ organization });
    };

    renderBillingGroupBadge = () => {
        const { organization } = this.state;
        const { billingGroup } = organization;

        if (!billingGroup) {
            return null;
        }

        return (
            <Chip
                component={Link}
                to={`${AppNavPaths.CompanyBillingGroupNew}/${billingGroup.id}`}
                target="_blank"
                color="white"
                className="billing-group-chip"
                text={(
                    <span className="d-inline-flex align-items-center">
                        <span
                            className="text-truncate mr-1"
                        >
                            {billingGroup.description}
                        </span>
                        <Can
                            I="edit"
                            a="billinggroup"
                        >
                            <FontAwesomeIcon
                                icon={faArrowUpRightFromSquare}
                                className="ml-1"
                                size="sm"
                            />
                        </Can>
                    </span>
                )}
            />
        );
    };

    renderDispatchGroupBadge = () => {
        const { organization } = this.state;
        const { dispatchGroup } = organization;

        if (!dispatchGroup) {
            return null;
        }

        return (
            <Chip
                component={Link}
                to={`${AppNavPaths.DispatchGroup}/${dispatchGroup.id}`}
                target="_blank"
                color="white"
                className="billing-group-chip"
                text={(
                    <span className="d-inline-flex align-items-center">
                        <span
                            className="text-truncate mr-1"
                        >
                            {dispatchGroup.name}
                        </span>

                        <Can
                            I="edit"
                            a="dispatch_group"
                        >
                            <FontAwesomeIcon
                                icon={faArrowUpRightFromSquare}
                                className="ml-1"
                                size="sm"
                            />
                        </Can>
                    </span>
                )}
            />
        );
    };


    renderHeaderRight = () => {
        const { organization, isCustomer, isDispatch } = this.state;
        const existing = Boolean(organization.id);

        const { billingGroup, dispatchGroup } = organization;

        if (!existing || (isCustomer && !billingGroup) || (isDispatch && !dispatchGroup)) {
            return null;
        }

        if (isCustomer) {
            return (
                <div className="d-flex align-items-center justify-content-end">
                    {this.renderBillingGroupBadge()}
                </div>
            );
        }

        if (isDispatch) {
            return (
                <div className="d-flex align-items-center justify-content-end">
                    {this.renderDispatchGroupBadge()}
                </div>
            );
        }

        return null;
    };

    onViewChangeHistory = () => {
        this.setState({ showCompanyHistory: true });
    };

    onCloseCompanyHistory = () => {
        this.setState({ showCompanyHistory: false });
    };

    getActions = () => {
        const { organization, isCustomer, isDispatch } = this.state;
        const existing = Boolean(organization.id);

        const { billingGroup, dispatchGroup } = organization;

        const actions = [];

        if (existing) {
            actions.push({
                key: 'view-change-history',
                onClick: this.onViewChangeHistory,
                icon: faTasks,
                label: 'View Change History',
                CanProps: {
                    I: 'history',
                    a: 'company',
                },
            });

            if (isCustomer && !billingGroup) {
                actions.push({
                    key: 'add-to-group',
                    label: 'Add to Billing Group',
                    icon: faPlus,
                    component: Link,
                    DropdownItemProps: {
                        to: AppNavPaths.CompanyBillingGroup,
                        target: '_blank',
                    },
                    CanProps: {
                        I: 'edit',
                        a: 'billinggroup',
                    },
                });
            }

            if (isDispatch && !dispatchGroup) {
                actions.push({
                    key: 'add-dispatch-group',
                    label: 'Add to Dispatch Group',
                    icon: faPlus,
                    component: Link,
                    DropdownItemProps: {
                        to: AppNavPaths.DispatchGroups,
                        target: '_blank',
                    },
                    CanProps: {
                        I: 'edit',
                        a: 'dispatch_group',
                    },
                });
            }
        }

        return actions.length > 0 ? actions : null;
    };

    render() {
        const {
            customers,
            subcontractorCustomersOptions,
            isCustomer,
            isBilling,
            isDispatch,
            isPayroll,
            isOperationsCenter,
            isSubcontractor,
            dispatchPortOptions,
            isParentCustomer,
            isProspect,
            isVBOffice,
            isDispatchPort,
            parentCustomers,
            parentCompanies,
            parentRequired,
            parentCompanyLabelText,
            organization,
            originalData,
            errors,
            formValidated,
            companyTypes,
            payPeriodTypes,
            saving,
            showContactsForm,
            selectedContact,
            chargeTypes,
            showRateFormDelete,
            contactFormIsReadOnly,
            invoiceTerms,
            invoiceMappings,
            chargeTypeGroups,
            allowContactTypeEdit,
            showMinWageRateFormDelete,
            usStates,
            countries,
            useInvoicingRequired,
            companyStatuses,
            zipPattern,
            showAddHolidayForm,
        } = this.state;


        if (!organization || (this.getOrganizationId() & !organization.id)) return '';

        const canEditCodes = this.canEditCodes();

        const invoiceCollationOptions = [
            {
                value: 0,
                label: 'No Collation (Individual invoice and timesheet files)',
            },
            {
                value: 1,
                label: 'Collated (Individual invoice files containing timesheets)',
            },
            {
                value: 2,
                label: 'Collated & Merged (invoices and timesheets merged into one file)',
            },
        ];

        const existing = Boolean(organization.id);
        const selectedSubcontractorCustomers = [
            ...(subcontractorCustomersOptions ?? []).filter((x) => (organization?.subcontractorCustomers ?? []).includes(
                x.value,
            )),
        ] ?? '';

        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};

        const isInvoicingAllowed = !!tenantSettings.invoicingEnabled;

        const isChargeTypeGroupsAllowed = !!tenantSettings.chargeTypeGroupsEnabled;
        const isBillingRatesAllowed = !!tenantSettings.billingRatesEnabled;
        const visibleGroupBillingRates = (organization.groupBillingRates ?? []).filter(
            (x) => x.chargeTypeGroupStatusId !== _ChargeTypeGroupStatuses.Inactive,
        );
        const contractExpirationEmailEnabled = Boolean(
            tenantSettings.contractExpiryDistributionList,
        );

        const regularShiftDifferentialEnabled = !!tenantSettings.enableRegularShiftDifferential;

        const tenantCountryId = this.context?.tenant?.countryId ?? null;
        const effectiveCountryId = tenantCountryId ?? organization.billingCountryId ?? null;

        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, organization)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    formShown={this.context.formIsOpen}
                    formId="organizationForm"
                    formHeadingIcon={faWarehouse}
                    formHeading={
                        !organization.id
                            ? 'New Organization'
                            : 'Edit Organization'
                    }
                    headerRight={this.renderHeaderRight()}
                    formName="organizationForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    onClearErrors={this.onClearErrors}
                    loading={this.state.loading}
                    backUrl={AppNavPaths.Organizations}
                    backLabel="Back to Organizations"
                    actions={this.getActions()}
                >
                    <ValidatedSelect
                        id="types"
                        name="organization.types"
                        label="Type(s)"
                        isMulti
                        required
                        options={companyTypes}
                        value={
                            (companyTypes ?? []).filter((x) => (organization.types ?? []).includes(
                                x.value,
                            )) ?? ''
                        }
                        onChange={this.onOrgTypeSelectChanged}
                        error="At least one organization type is required."
                    />

                    <ValidatedSelect
                        id="companyStatusId"
                        name="organization.companyStatusId"
                        label="Status"
                        required
                        error="A status must be selected"
                        options={companyStatuses}
                        value={
                            companyStatuses?.find(
                                (s) => s.value
                                    === organization.companyStatusId,
                            ) ?? ''
                        }
                        onChange={this.onSelectChanged}
                    />

                    {isCustomer && (
                        <FormGroup>
                            <FormCheckbox
                                small
                                id="isNationalAccount"
                                checked={
                                    organization.isNationalAccount
                                }
                                onChange={(e) => {
                                    const { organization } = this.state;
                                    organization.isNationalAccount = e.target.checked;
                                    this.setState({
                                        organization,
                                    });
                                }}
                                labelText="Is National Account"
                            />
                        </FormGroup>
                    )}

                    <FormDivider />

                    <Input
                        label="Name"
                        id="organizationName"
                        name="organization.name"
                        value={organization.name ?? ''}
                        onChange={this.onChange}
                        placeholder="Enter Name (max 150 characters)"
                        maxLength="150"
                        pattern="[^()\/><\][\\\x22;|]+"
                        required
                        error="Name is required and can only contain the following special characters: hyphens and periods."
                    />

                    <AddressInput
                        className="org-address-input"
                        namePrefix="organization"
                        onChange={this.onChange}
                        address={organization}
                        onStateChanged={this.onStateChange}
                        onCountryChanged={this.onCountryChange}
                        showCountryInput={tenantCountryId === null}
                        single
                    />
                    {(!!isDispatch || isBilling) && (
                        <Input
                            label="Phone"
                            id="phone"
                            placeholder="ex: 555-555-5555"
                            name="organization.phone"
                            pattern={patterns.htmlPhone}
                            autoComplete="off"
                            type="tel"
                            onChange={this.onPhoneChanged}
                            value={organization.phone ?? ''}
                            required
                            error="A valid phone number is required. (555-555-5555)"
                        />
                    )}

                    {!!(tenantSettings ?? {}).enableAlternateBillTo && !isDispatchPort && (
                        <>
                            <FormCheckbox
                                small
                                id="useAsBillingAddress"
                                checked={
                                    organization.useAsBillingAddress
                                }
                                onChange={(e) => {
                                    const { organization } = this.state;
                                    organization.useAsBillingAddress = e.target.checked;
                                    this.setState({
                                        organization,
                                    });
                                }}
                                labelText="Also Use For Billing Address"
                            />

                            {!organization.useAsBillingAddress && (
                                <>
                                    <FormGroup>
                                        <SubHeading className="mt-4">
                                            Billing Address
                                        </SubHeading>
                                    </FormGroup>
                                    <Input
                                        label="Name"
                                        id="billingName"
                                        name="organization.billingName"
                                        autoComplete="off"
                                        value={organization.billingName ?? ''}
                                        onChange={this.onChange}
                                    />
                                    <Input
                                        label="Address Line 1"
                                        id="billingAddress1"
                                        name="organization.billingAddress1"
                                        autoComplete="off"
                                        value={organization.billingAddress1 ?? ''}
                                        onChange={this.onChange}
                                    />
                                    <Input
                                        label="Address Line 2"
                                        id="billingAddress2"
                                        name="organization.billingAddress2"
                                        autoComplete="off"
                                        value={organization.billingAddress2 ?? ''}
                                        onChange={this.onChange}
                                    />
                                    <Input
                                        label="City"
                                        id="billingCity"
                                        name="organization.billingCity"
                                        autoComplete="off"
                                        value={organization.billingCity ?? ''}
                                        onChange={this.onChange}
                                    />

                                    {tenantCountryId === null && (
                                        <ValidatedSelect
                                            id="billingCountry"
                                            name="organization.billingCountry"
                                            label="Country"
                                            options={countries}
                                            value={
                                                countries?.find(
                                                    (s) => s.value
                                                        === organization.billingCountryCode,
                                                ) ?? ''
                                            }
                                            onChange={
                                                this.onBillingCountryChanged
                                            }
                                        />
                                    )}

                                    <ValidatedSelect
                                        id="billingState"
                                        name="organization.billingState"
                                        label="State"
                                        options={usStates}
                                        value={
                                            usStates?.find(
                                                (s) => s.value
                                                    === organization.billingState,
                                            ) ?? ''
                                        }
                                        onChange={
                                            this.onBillingStateChanged
                                        }
                                        isDisabled={!effectiveCountryId}
                                    />

                                    <Input
                                        label="Zip Code"
                                        id="billingZip"
                                        name="organization.billingZip"
                                        autoComplete="off"
                                        pattern={zipPattern}
                                        value={organization.billingZip ?? ''}
                                        onChange={this.onChange}
                                        error={!organization.billingZip ? 'ZIP Code is required.' : 'ZIP Code is invalid.'}
                                    />
                                </>
                            )}
                        </>
                    )}

                    <FormGroup
                        hidden={
                            !isBilling
                            && !isCustomer
                            && !isDispatch
                            && !isPayroll
                            && !isPayroll
                            && !isVBOffice
                        }
                    >
                        <SubHeading className="mt-4">
                            Configurations
                        </SubHeading>
                    </FormGroup>

                    {!tenantSettings.quirksEnabled && (
                        <FormGroup hidden={!isDispatch}>
                            <FormLabel
                                htmlFor="hasCustomShiftDifferential"
                                label="Does this office have its own shift differential hours?"
                                required
                            />

                            <TipAlert
                                containerClassName="mt-1 mb-1"
                                label={(
                                    <>
                                        Enabling this option and leaving these fields blank will disable shift differential calculations for this office.

                                        {Boolean(regularShiftDifferentialEnabled) && (
                                            <>
                                                {' '}
                                                Your organization&apos;s default shift differential hours are
                                                {' '}

                                                <strong>{getDate(tenantSettings.regularShiftDifferentialStart, 'h:mm a')}</strong>
                                                {' to '}
                                                <strong>{getDate(tenantSettings.regularShiftDifferentialEnd, 'h:mm a')}</strong>
                                                .
                                            </>
                                        )}
                                    </>
                                )}
                            />

                            <YesNoButtons
                                name="hasCustomShiftDifferential"
                                value={organization.hasCustomShiftDifferential}
                                onClick={(value) => {
                                    const { organization } = this.state;
                                    organization.hasCustomShiftDifferential = value;

                                    this.setState({
                                        organization,
                                    });
                                }}
                            />

                            <Collapse isOpen={organization.hasCustomShiftDifferential}>
                                <Block className="mt-3">
                                    <Row>
                                        <Col>
                                            <div
                                                className="ml-4"
                                                style={{
                                                    width: '100px',
                                                }}
                                            >
                                                <FormLabel
                                                    htmlFor="customShiftDifferentialStart"
                                                    label="Shift starts at"
                                                />

                                                <TimePicker
                                                    id="customShiftDifferentialStart"
                                                    name="customShiftDifferentialStart"
                                                    increment={0.25}
                                                    value={organization.customShiftDifferentialStart}
                                                    onChange={this.onTimeChanged}
                                                />
                                            </div>
                                        </Col>
                                        <Col>
                                            <div
                                                className="ml-1"
                                                style={{
                                                    width: '100px',
                                                }}
                                            >
                                                <FormLabel
                                                    htmlFor="customShiftDifferentialEnd"
                                                    label="Shift ends at"
                                                />

                                                <TimePicker
                                                    id="customShiftDifferentialEnd"
                                                    name="customShiftDifferentialEnd"
                                                    increment={0.25}
                                                    value={organization.customShiftDifferentialEnd}
                                                    onChange={this.onTimeChanged}
                                                />
                                            </div>
                                        </Col>
                                        <Col>
                                            <div
                                                className="ml-1"
                                                style={{
                                                    width: '200px',
                                                }}
                                            >
                                                <NumericInput
                                                    id="organization.customShiftDifferentialPayAdjustment"
                                                    label="Shift Differential Pay Adjustment"
                                                    name="organization.customShiftDifferentialPayAdjustment"
                                                    value={
                                                        organization.customShiftDifferentialPayAdjustment
                                                        ?? ''
                                                    }
                                                    startAddon={(
                                                        <span className="text-muted">$</span>
                                                    )}
                                                    onChange={this.onChange}
                                                    placeholder="Enter an amount"
                                                    step={0.01}
                                                    min={0}
                                                />
                                            </div>
                                        </Col>

                                        <Col />
                                        <Col />
                                        <Col />
                                        <Col />
                                        <Col />
                                    </Row>
                                </Block>
                            </Collapse>
                        </FormGroup>
                    )}

                    {Boolean(isDispatch) && !!existing && dispatchPortOptions.length > 1 && (
                        <ValidatedSelect
                            id="organization.defaultDispatchPortId"
                            name="organization.defaultDispatchPortId"
                            label="Default Dispatch Port"
                            options={dispatchPortOptions}
                            value={dispatchPortOptions.find((o) => o.value === organization.defaultDispatchPortId) ?? null}
                            onChange={this.onDefaultDispatchPortChanged}
                        />
                    )}

                    {Boolean(isDispatch) && tenantSettings.payPeriodTypeId === PayPeriodTypes.Biweekly && (
                        <Row>
                            <FormGroupColumn>
                                <ValidatedSelect
                                    id="organization.payPeriodTypeId"
                                    name="organization.payPeriodTypeId"
                                    label="How is OT calculated?"
                                    options={payPeriodTypes}
                                    readOnly={!canEditCodes}
                                    value={
                                        (payPeriodTypes ?? []).find(
                                            (x) => organization.payPeriodTypeId
                                                === x.value,
                                        ) ?? ''
                                    }
                                    onChange={this.onSelectChanged}
                                    error="A pay period duration selection is required."
                                    required
                                    hideClear
                                />
                            </FormGroupColumn>

                            <FormGroupColumn>
                                <NumericInput
                                    label={organization.payPeriodTypeId === PayPeriodTypes.Weekly ? 'Weekly Overtime Threshold' : 'Biweekly Overtime Threshold'}
                                    id="organization.payPeriodOvertimeThreshold"
                                    name="organization.payPeriodOvertimeThreshold"
                                    readOnly={!canEditCodes}
                                    value={
                                        organization.payPeriodOvertimeThreshold
                                        ?? ''
                                    }
                                    placeholder="Hours"
                                    onChange={this.onChange}
                                    step={0.01}
                                    min={0}
                                    required
                                />
                            </FormGroupColumn>
                        </Row>
                    )}
                    <Input
                        label="Customer TCC Code"
                        formGroupHidden={!isCustomer}
                        id="tccCode"
                        name="organization.tccCode"
                        onChange={this.onChange}
                        readOnly={!canEditCodes}
                        value={organization.tccCode ?? ''}
                    />
                    <Input
                        label="NetSuite ID"
                        formGroupHidden={!(isDispatch || isCustomer || isVBOffice)}
                        id="externalIdMapping"
                        name="organization.externalId"
                        onChange={this.onChange}
                        readOnly={!canEditCodes}
                        value={organization.externalId ?? ''}
                    />
                    {isSubcontractor && (
                        <Input
                            label="NetSuite Subcontractor ID"
                            formGroupHidden={!(isSubcontractor)}
                            id="subcontractorExternalIdMapping"
                            name="organization.subcontractorExternalId"
                            onChange={this.onChange}
                            readOnly={!canEditCodes}
                            value={organization.subcontractorExternalId ?? ''}
                        />
                    )}

                    {isCustomer && (
                        <>
                            <NumericInput
                                label="Minimum Bill Hours for Flagging Charges"
                                formGroupClassName="mt-2"
                                id="minimumBillUnits"
                                name="organization.minimumBillUnits"
                                value={organization.minimumBillUnits ?? {}}
                                onChange={this.onChange}
                                placeholder="Enter Minimum Bill Hours"
                                step="0.25"
                                preventNegative
                                min={0}
                            />
                            <UncontrolledTooltip
                                placement="left"
                                target="minimumBillUnits"
                            >
                                Minimum Bill Hours applies only to charges where Track Start Time is set to Flagging.
                            </UncontrolledTooltip>
                            <FormGroup>
                                <FormLabel
                                    className="w-100"
                                    htmlFor="dailyReportEmailList"
                                    label="Billing"
                                />
                                <FormCheckbox
                                    small
                                    id="isCancelledJobBillingDisabled"
                                    checked={
                                        organization.isCancelledJobBillingDisabled
                                    }
                                    onChange={(e) => {
                                        const { organization } = this.state;
                                        organization.isCancelledJobBillingDisabled = e.target.checked;
                                        if (e.target.checked) {
                                            organization.cancellationBillQty = null;
                                        }
                                        this.setState({
                                            organization,
                                        });
                                    }}
                                    labelText="Disable Billing for Cancelled Jobs"
                                />

                                {!organization.isCancelledJobBillingDisabled && (
                                    <>
                                        <FormCheckbox
                                            small
                                            className="mt-2"
                                            id="cancellationsAlwaysStraightTime"
                                            checked={
                                                organization.cancellationsAlwaysStraightTime
                                            }
                                            onChange={(e) => {
                                                const { organization } = this.state;
                                                organization.cancellationsAlwaysStraightTime = e.target.checked;
                                                this.setState({
                                                    organization,
                                                });
                                            }}
                                            labelText="Cancellations are always Straight Time"
                                        />
                                        <NumericInput
                                            label="Cancellation Bill Qty"
                                            formGroupClassName="mt-3"
                                            id="cancellationBillQty"
                                            name="organization.cancellationBillQty"
                                            value={organization.cancellationBillQty ?? ''}
                                            onChange={this.onChange}
                                            placeholder="Enter Cancellation Bill Qty"
                                            step="0.25"
                                            preventNegative
                                            min={0}
                                        />
                                        <UncontrolledTooltip
                                            placement="left"
                                            target="cancellationBillQty"
                                        >
                                            Cancellation Bill Qty applies only to charges where Track Start Time is set to Flagging.
                                        </UncontrolledTooltip>
                                    </>
                                )}
                            </FormGroup>
                            <FormGroup>
                                <FormCheckbox
                                    small
                                    id="isIntercompany"
                                    checked={
                                        organization.isIntercompany
                                    }
                                    onChange={(e) => {
                                        const { organization } = this.state;
                                        organization.isIntercompany = e.target.checked;
                                        this.setState({
                                            organization,
                                        });
                                    }}
                                    labelText="Is Intercompany customer"
                                />
                            </FormGroup>
                        </>
                    )}

                    {((isCustomer && organization.isIntercompany) || isBilling) && (
                        <>
                            <Input
                                label="NetSuite Subsidiary ID"
                                id="subsidiaryId"
                                name="organization.subsidiaryExternalId"
                                onChange={this.onChange}
                                readOnly={!this.canEditCodes()}
                                value={organization.subsidiaryExternalId ?? ''}
                                required={isCustomer && organization.isIntercompany}
                                error="Subsidiary ID is required for Intercompany Customer."
                            />
                            <Input
                                label="Billing Prefix"
                                id="externalCode"
                                name="organization.externalCode"
                                onChange={this.onChange}
                                readOnly={!this.canEditCodes()}
                                value={organization.externalCode ?? ''}
                                required={isCustomer && organization.isIntercompany}
                                error="Billing Prefix is required for Intercompany Customer."
                            />
                        </>
                    )}

                    <Input
                        label="Payroll ADP ID"
                        formGroupHidden={!isPayroll}
                        id="payrollExternalId"
                        name="organization.payrollExternalId"
                        onChange={this.onChange}
                        readOnly={!canEditCodes}
                        value={organization.payrollExternalId ?? ''}
                    />

                    <Input
                        label="Business Number"
                        id="businessNumber"
                        name="organization.businessNumber"
                        onChange={this.onChange}
                        readOnly={!canEditCodes}
                        value={organization.businessNumber ?? ''}
                    />

                    {!!isPayroll && !!tenantSettings.quirksEnabled && (
                        <FormCheckbox
                            small
                            className="ml-3 mt-2"
                            id="enablePayrollApplicationExport"
                            checked={
                                organization.enablePayrollApplicationExport
                            }
                            onChange={(e) => {
                                const { organization } = this.state;
                                organization.enablePayrollApplicationExport = e.target.checked;
                                this.setState({
                                    organization,
                                });
                            }}
                            labelText="Enable Tenant's Payroll Application Export"
                        />
                    )}

                    <Input
                        label="Dispatch ADP ID"
                        formGroupHidden={!isDispatch}
                        id="dispatchExternalId"
                        name="organization.dispatchExternalId"
                        onChange={this.onChange}
                        readOnly={!canEditCodes}
                        value={organization.dispatchExternalId ?? ''}
                    />

                    <FormGroup
                        hidden={!isDispatch || !canEditCodes}
                        className="mt-2 mb-4"
                    >
                        <FormLabel
                            className="w-100"
                            htmlFor="dailyReportEmailList"
                            label="Daily Report Email Recipients"
                        />

                        <TipAlert
                            label={(
                                <>
                                    Enter addresses that should receive daily operational reports for this dispatch office.
                                    Reports are sent each day at 9:30am, 5:30pm and 7:30pm.
                                </>
                            )}
                        />

                        <EmailRecipients
                            currentEmailAddress={
                                this.state.currentDailyReportEmailList
                            }
                            onEmailAdded={this.onEmailAdded}
                            onEmailChanged={this.onEmailChanged}
                            onEmailRemoved={this.onEmailRemoved}
                            emailAddresses={
                                organization.dailyReportEmailList?.split(',') ?? []
                            }
                            InputProps={{
                                startAddon: (
                                    <>
                                        <IconButton
                                            id="download-operational-report"
                                            onClick={this.downloadDailyOperationalReport}
                                        >
                                            <FontAwesomeIcon
                                                icon={faDownload}
                                                className="text-muted"
                                            />
                                        </IconButton>

                                        <UncontrolledTooltip target="download-operational-report">
                                            Download Daily Operational Report
                                        </UncontrolledTooltip>
                                    </>
                                ),
                            }}
                        />
                    </FormGroup>

                    <FormGroup
                        hidden={!isDispatch || !canEditCodes}
                        className="mt-2 mb-4"
                    >
                        <FormLabel
                            className="w-100"
                            htmlFor="handoffEmailRecipients"
                            label="Handoff Email Recipients"
                        />

                        <TipAlert
                            label={(
                                <>
                                    Enter addresses that should receive handoff emails for this dispatch office.
                                </>
                            )}
                        />

                        <EmailRecipients
                            currentEmailAddress={
                                this.state.currentHandoffEmailRecipients
                            }
                            onEmailAdded={this.onHandoffEmailAdded}
                            onEmailChanged={this.onHandoffEmailChanged}
                            onEmailRemoved={this.onHandoffEmailRemoved}
                            emailAddresses={
                                organization.handoffEmailRecipients?.split(',') ?? []
                            }
                        />
                    </FormGroup>

                    <FormGroup
                        hidden={!isDispatch || !canEditCodes}
                        className="mt-2 mb-4"
                    >
                        <FormLabel
                            className="w-100"
                            htmlFor="notificationEmailRecipients"
                            label="Notification Email Recipients"
                        />

                        <TipAlert
                            label={(
                                <>
                                    Enter addresses that should receive job creation, timesheet rejection and employee call off notifications for this dispatch office.
                                </>
                            )}
                        />

                        <EmailRecipients
                            currentEmailAddress={
                                this.state.currentNotificationEmailRecipients
                            }
                            onEmailAdded={this.onNotificationEmailAdded}
                            onEmailChanged={this.onNotificationEmailChanged}
                            onEmailRemoved={this.onNotificationEmailRemoved}
                            emailAddresses={
                                organization.notificationEmailRecipients?.split(',') ?? []
                            }
                        />
                    </FormGroup>

                    <ValidatedSelect
                        id="customerCompanyId"
                        name="organization.customerCompanyId"
                        label="Customer"
                        required={
                            !!isOperationsCenter && !isCustomer
                        }
                        options={customers}
                        value={
                            (customers ?? []).find(
                                (x) => organization.customerCompanyId
                                    === x.value,
                            ) ?? ''
                        }
                        onChange={this.onSelectChanged}
                        error="A customer selection is required when the organization types include [Operations Center]."
                        formGroupHidden={
                            !isOperationsCenter
                            || !!isCustomer
                            || !!isParentCustomer
                            || !!isProspect
                        }
                    />

                    {(isCustomer
                        || isParentCustomer
                        || isOperationsCenter) && (
                            <FormGroup>
                                {(companyTypes ?? []).filter(
                                    (x) => x.value
                                        == OrganizationTypes.Customer,
                                ).length > 0
                                    && !isProspect
                                    && !isParentCustomer
                                    && !isOperationsCenter && (
                                        <FormCheckbox
                                            small
                                            className="ml-3 mt-2"
                                            id="operationsCentersApply"
                                            checked={
                                                organization.operationsCentersApply
                                            }
                                            onChange={(e) => {
                                                const { organization } = this.state;
                                                organization.operationsCentersApply = e.target.checked;
                                                this.setState({
                                                    organization,
                                                });
                                            }}
                                            labelText="Assign Contracts to Operations Centers"
                                        />
                                    )}
                                {(companyTypes ?? []).filter(
                                    (x) => x.value
                                        == OrganizationTypes.CustomerSubcontractor,
                                ).length > 0
                                    && !isProspect
                                    && !isParentCustomer
                                    && !isOperationsCenter && (
                                        <FormCheckbox
                                            small
                                            className="ml-3 mt-2"
                                            id="subcontractorsApply"
                                            checked={
                                                organization.subcontractorsApply
                                            }
                                            onChange={(e) => {
                                                const { organization } = this.state;
                                                organization.subcontractorsApply = e.target.checked;
                                                this.setState({
                                                    organization,
                                                });
                                            }}
                                            labelText="Assign Jobs to Customer Subcontractors"
                                        />
                                    )}
                                {(parentCustomers ?? []).length > 0
                                    && !isOperationsCenter && (
                                        <FormCheckbox
                                            small
                                            className="ml-3 mt-2"
                                            id="parentCustomerApply"
                                            checked={
                                                organization.parentCustomerApply
                                            }
                                            onChange={(e) => {
                                                const { organization } = this.state;
                                                organization.parentCustomerApply = e.target.checked;

                                                if (
                                                    !organization.parentCustomerApply
                                                ) {
                                                    organization.parentCustomerId = null;
                                                }

                                                this.setState({
                                                    organization,
                                                });
                                            }}
                                            labelText="Assign Parent Customer"
                                        />
                                    )}

                                {isCustomer
                                    && contractExpirationEmailEnabled && (
                                        <FormCheckbox
                                            small
                                            className="ml-3 mt-2"
                                            id="exemptFromExpiringContractEmailNotifications"
                                            checked={
                                                organization.exemptFromExpiringContractEmailNotifications
                                            }
                                            onChange={(e) => {
                                                const { organization } = this.state;
                                                organization.exemptFromExpiringContractEmailNotifications = e.target.checked;
                                                this.setState({
                                                    organization,
                                                });
                                            }}
                                            labelText="Exempt From Expiring Contract Email Notifications"
                                        />
                                    )}

                                {isCustomer && (
                                    <FormCheckbox
                                        small
                                        className="ml-3 mt-2"
                                        id="chkExpandedCancellationReasonRequired"
                                        checked={
                                            organization.expandedCancellationReasonRequired
                                        }
                                        onChange={(e) => {
                                            const { organization } = this.state;
                                            organization.expandedCancellationReasonRequired = e.target.checked;
                                            this.setState({
                                                organization,
                                            });
                                        }}
                                        labelText="Expanded Cancellation Reason Required"
                                    />
                                )}

                                {tenantSettings.enableCustomizedHoliday && existing && isCustomer && (
                                    <div className="ml-3 mt-2 d-flex flex-column">
                                        <FormCheckbox
                                            id="defineCustomHolidays"
                                            labelText="Define Custom Holidays"
                                            checked={this.state.organization.hasCustomHolidayList}
                                            onChange={this.onDefineCustomHolidaysChanged}
                                            small
                                        />

                                        {this.state.organization.hasCustomHolidayList && (
                                            <ValidatedSelect
                                                id="companyHoliday"
                                                name="organization.companyHoliday"
                                                label="Define Company Holidays"
                                                formGroupClassName="mt-2"
                                                isMulti
                                                options={this.state.holidays}
                                                value={this.state.selectedHolidays}
                                                onChange={(newValue) =>
                                                    this.setState({ selectedHolidays: newValue || [] })
                                                }
                                                helperText={this.state.selectedHolidays.length === 0 && (
                                                    'No holidays selected - this company will be treated as observing none.'
                                                )}
                                                showAddButton
                                                onAddButtonClick={() => this.onAddHoliday()}
                                            />
                                        )}
                                    </div>
                                )}

                                {/* Customized Invoicing Email (customers only) */}
                                {isCustomer &&
                                    !!(tenantSettings ?? {}).billingEnabled &&
                                    isInvoicingAllowed && (
                                        <FormCheckbox
                                            small
                                            className="ml-3 mt-2"
                                            id="customizedInvoicingEmail"
                                            checked={!!organization.customizeEmailSubject}
                                            onChange={(e) => {
                                                const { organization } = this.state;
                                                organization.customizeEmailSubject = e.target.checked;
                                                this.setState({ organization });
                                            }}
                                            labelText="Customized Invoicing Email"
                                        />
                                    )}
                                {isCustomer && (
                                    <FormCheckbox
                                        small
                                        className="ml-3 mt-2"
                                        id="allowTimesheetAttachments"
                                        checked={!!organization.allowTimesheetAttachments}
                                        onChange={(e) => {
                                            const { organization } = this.state;
                                            organization.allowTimesheetAttachments = e.target.checked;
                                            this.setState({ organization });
                                        }}
                                        labelText="Allow Timesheet Attachments"
                                    />
                                )}
                                {!!(tenantSettings ?? {}).billingEnabled
                                    && isInvoicingAllowed
                                    && (!isOperationsCenter
                                        || (isOperationsCenter
                                            && useInvoicingRequired))
                                    && (
                                        <FormCheckbox
                                            small
                                            className="ml-3 mt-2"
                                            id="useInvoicing"
                                            checked={
                                                organization.useInvoicing
                                            }
                                            onChange={(e) => {
                                                const { organization } = this.state;
                                                organization.useInvoicing = e.target.checked;
                                                this.setState({
                                                    organization,
                                                });
                                            }}
                                            labelText="Use Invoicing"
                                        />
                                    )}
                                {!!(tenantSettings ?? {}).billingEnabled
                                    && isInvoicingAllowed
                                    && this.state.organization.useInvoicing
                                    && !isOperationsCenter && (
                                        <>
                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="operationsCenterUseInvoicing"
                                                checked={
                                                    organization.operationsCenterUseInvoicing
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.operationsCenterUseInvoicing = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText='Require "Use Invoicing" Option on Operation Centers'
                                            />

                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="invoiceHasPO"
                                                checked={
                                                    organization.invoiceHasPO
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.invoiceHasPO = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Include PO on Invoice"
                                            />
                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="groupingChargesPerInvoice"
                                                checked={
                                                    organization.groupingChargesPerInvoice
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.groupingChargesPerInvoice = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Grouping Charges per Invoice"
                                            />
                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="invoice"
                                                checked={
                                                    organization.invoiceUsesWorkRequest
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.invoiceUsesWorkRequest = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Include Work Request on Invoice"
                                            />
                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="invoiceHasInspectorName"
                                                checked={
                                                    organization.invoiceHasInspectorName
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.invoiceHasInspectorName = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Include Inspector's Name on Invoice"
                                            />
                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="includeDispatchOfficeInInvoiceFilename"
                                                checked={
                                                    organization.includeDispatchOfficeInInvoiceFilename
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.includeDispatchOfficeInInvoiceFilename = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Include Dispatch Office Name in Invoice Filename"
                                            />
                                            {!tenantSettings.hideInvoicePrefixOnPrint && (
                                                <FormCheckbox
                                                    small
                                                    className="ml-5 mt-2"
                                                    id="hideInvoicePrefixOnPrint"
                                                    checked={
                                                        organization.hideInvoicePrefixOnPrint
                                                    }
                                                    onChange={(e) => {
                                                        const { organization } = this.state;
                                                        organization.hideInvoicePrefixOnPrint = e.target.checked;
                                                        this.setState({
                                                            organization,
                                                        });
                                                    }}
                                                    labelText="Hide Invoice Prefix On Print"
                                                />
                                            )}
                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="billFirstPartialPeriod"
                                                checked={
                                                    organization.billFirstPartialPeriod
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.billFirstPartialPeriod = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Bill First Partial Period"
                                            />
                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="invoiceTimesheetUsesWO"
                                                checked={
                                                    organization.invoiceTimesheetUsesWO
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.invoiceTimesheetUsesWO = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Use TC# as timesheet file name"
                                            />

                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="allowMultipleWONumbers"
                                                checked={
                                                    organization.allowMultipleWONumbers
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.allowMultipleWONumbers = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Allow Multiple Workorders"
                                            />

                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="embedCrewLeadWorkCardInInvoice"
                                                checked={
                                                    organization.embedCrewLeadWorkCardInInvoice
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.embedCrewLeadWorkCardInInvoice = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Embed crew lead work card in invoice timesheet PDF"
                                            />

                                            {Boolean(tenantSettings.enableDoubleTimeBilling) && (
                                                <>
                                                    <FormCheckbox
                                                        small
                                                        className="ml-5 mt-2"
                                                        id="billingEnableDT"
                                                        checked={
                                                            organization.billingEnableDT
                                                        }
                                                        onChange={(e) => {
                                                            const { organization } = this.state;
                                                            organization.billingEnableDT = e.target.checked;
                                                            this.setState({
                                                                organization,
                                                            });
                                                        }}
                                                        labelText="Enable DT Billing for Contracts"

                                                    />

                                                    {Boolean(tenantSettings.enforceContractUnionWork) && (
                                                        <UncontrolledTooltip
                                                            target="billingEnableDT"
                                                        >
                                                            This setting only affects non-union contract work.
                                                            Union contracts automatically have DT billing enabled.
                                                        </UncontrolledTooltip>
                                                    )}
                                                </>
                                            )}

                                            {Boolean(tenantSettings.enableBillPeriods) && (
                                                <FormCheckbox
                                                    small
                                                    className="ml-5 mt-2"
                                                    id="useProodOf40"
                                                    checked={
                                                        organization.useProofOf40
                                                    }
                                                    onChange={(e) => {
                                                        const { organization } = this.state;
                                                        organization.useProofOf40 = e.target.checked;
                                                        this.setState({
                                                            organization,
                                                        });
                                                    }}
                                                    labelText="Use Proof Of 40"
                                                />
                                            )}

                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="includeTimesheetNumberInLineItems"
                                                checked={
                                                    organization.includeTimesheetNumberInLineItems
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.includeTimesheetNumberInLineItems = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Include Timesheet Number in Line Items"
                                            />

                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="includeRequestorNameOnInvoice"
                                                checked={
                                                    organization.includeRequestorNameOnInvoice
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.includeRequestorNameOnInvoice = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Include Requestor Name on Invoice"
                                            />

                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="includeDueDateOnInvoice"
                                                checked={
                                                    organization.includeDueDateOnInvoice
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.includeDueDateOnInvoice = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Include Due Date on Invoice"
                                            />

                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="includeStartEndTimeOnInvoice"
                                                checked={
                                                    organization.includeStartEndTimeOnInvoice
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.includeStartEndTimeOnInvoice = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Include Start/End Time on Invoice"
                                            />

                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="showTauliaContactOnInvoice"
                                                checked={
                                                    organization.showTauliaContactOnInvoice
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.showTauliaContactOnInvoice = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Show Taulia Contact on Invoice"
                                            />

                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="showPointOfContactOnInvoice"
                                                checked={
                                                    organization.showPointOfContactOnInvoice
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.showPointOfContactOnInvoice = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Show Point of Contact on Invoice"
                                            />

                                            {Boolean(tenantSettings.enableShiftDifferentialBilling) && (
                                                <>
                                                    <FormCheckbox
                                                        small
                                                        className="ml-5 mt-2"
                                                        id="hasCustomShiftDifferential"
                                                        checked={
                                                            organization.hasCustomShiftDifferential
                                                        }
                                                        onChange={(e) => {
                                                            const { organization } = this.state;
                                                            organization.hasCustomShiftDifferential = e.target.checked;
                                                            this.setState({
                                                                organization,
                                                            });
                                                        }}
                                                        labelText="Customer has shift differential hours"
                                                    />

                                                    <Collapse isOpen={organization.hasCustomShiftDifferential}>
                                                        <Block className="ml-5 mt-3 mb-2">
                                                            <Row>
                                                                <Col>
                                                                    <div
                                                                        className="ml-4"
                                                                        style={{
                                                                            width: '100px',
                                                                        }}
                                                                    >
                                                                        <FormLabel
                                                                            htmlFor="customShiftDifferentialStart"
                                                                            label="Shift starts at"
                                                                        />

                                                                        <TimePicker
                                                                            id="customShiftDifferentialStart"
                                                                            name="customShiftDifferentialStart"
                                                                            increment={0.25}
                                                                            value={organization.customShiftDifferentialStart}
                                                                            onChange={this.onTimeChanged}
                                                                        />
                                                                    </div>
                                                                </Col>
                                                                <Col>
                                                                    <div
                                                                        className="ml-1"
                                                                        style={{
                                                                            width: '100px',
                                                                        }}
                                                                    >
                                                                        <FormLabel
                                                                            htmlFor="customShiftDifferentialEnd"
                                                                            label="Shift ends at"
                                                                        />

                                                                        <TimePicker
                                                                            id="customShiftDifferentialEnd"
                                                                            name="customShiftDifferentialEnd"
                                                                            increment={0.25}
                                                                            value={organization.customShiftDifferentialEnd}
                                                                            onChange={this.onTimeChanged}
                                                                        />
                                                                    </div>
                                                                </Col>

                                                                <Col />
                                                                <Col />
                                                                <Col />
                                                                <Col />
                                                                <Col />
                                                                <Col />
                                                                <Col />
                                                            </Row>
                                                        </Block>
                                                    </Collapse>
                                                </>
                                            )}

                                            <FormCheckbox
                                                small
                                                className="ml-5 mt-2"
                                                id="useContractSchedule"
                                                checked={
                                                    organization.useContractSchedule
                                                }
                                                onChange={(e) => {
                                                    const { organization } = this.state;
                                                    organization.useContractSchedule = e.target.checked;
                                                    this.setState({
                                                        organization,
                                                    });
                                                }}
                                                labelText="Use the Contract Schedule for OT"

                                            />

                                            <Collapse isOpen={!organization.useContractSchedule}>
                                                <FormGroup>
                                                    {Boolean(tenantSettings.enableBillPeriods) && (
                                                        <FormCheckbox
                                                            small
                                                            className="ml-5 mt-2"
                                                            id="billingUsesOT"
                                                            checked={
                                                                organization.billingUsesOT
                                                            }
                                                            onChange={(e) => {
                                                                const { organization } = this.state;
                                                                organization.billingUsesOT = e.target.checked;
                                                                this.setState({
                                                                    organization,
                                                                });
                                                            }}
                                                            labelText="40 Hours Overtime"
                                                        />
                                                    )}

                                                    <FormCheckbox
                                                        small
                                                        className="ml-5 mt-2"
                                                        id="billingUseDailyOT"
                                                        checked={
                                                            organization.billingUseDailyOT
                                                        }
                                                        onChange={(e) => {
                                                            const { organization } = this.state;
                                                            organization.billingUseDailyOT = e.target.checked;
                                                            this.setState({
                                                                organization,
                                                            });
                                                        }}
                                                        labelText="Daily Overtime"
                                                    />
                                                </FormGroup>
                                            </Collapse>
                                        </>
                                    )}
                            </FormGroup>
                        )}

                    {isCustomer
                        && isInvoicingAllowed
                        && !isOperationsCenter && (
                            <>
                                {this.state.organization.useInvoicing
                                    && organization.billingUseDailyOT
                                    && !organization.useContractSchedule && (
                                        <NumericInput
                                            label="Daily Overtime Threshold"
                                            formGroupClassName="ml-5 mt-2"
                                            id="billingDailyOTThreshold"
                                            name="organization.billingDailyOTThreshold"
                                            value={
                                                organization.billingDailyOTThreshold
                                                ?? ''
                                            }
                                            onChange={this.onChange}
                                            placeholder="Hours"
                                            required
                                        />
                                    )}

                                {this.state.organization
                                    .useInvoicing && (
                                        <>
                                            <ValidatedSelect
                                                id="invoiceMappingId"
                                                name="organization.invoiceMappingId"
                                                label="Invoice Charge Mapping"
                                                formGroupClassName="ml-5 mt-2"
                                                options={
                                                    invoiceMappings
                                                }
                                                value={
                                                    (
                                                        invoiceMappings
                                                        ?? []
                                                    ).find(
                                                        (x) => organization.invoiceMappingId
                                                            === x.value,
                                                    ) ?? ''
                                                }
                                                onChange={
                                                    this.onSelectChanged
                                                }
                                            />

                                            <ValidatedSelect
                                                id="invoiceCollationId"
                                                name="organization.invoiceCollationId"
                                                formGroupClassName="ml-5 mt-2"
                                                label="Invoice Collation"
                                                options={
                                                    invoiceCollationOptions
                                                }
                                                value={
                                                    (
                                                        invoiceCollationOptions
                                                        ?? []
                                                    ).find(
                                                        (x) => organization.invoiceCollationId
                                                            === x.value,
                                                    ) ?? ''
                                                }
                                                onChange={
                                                    this.onSelectChanged
                                                }
                                            />

                                            <ValidatedSelect
                                                id="invoiceTermsId"
                                                name="organization.invoiceTermsId"
                                                formGroupClassName="ml-5 mt-2"
                                                label="Payment Terms"
                                                options={invoiceTerms}
                                                value={
                                                    (
                                                        invoiceTerms
                                                        ?? []
                                                    ).find(
                                                        (x) => organization.invoiceTermsId
                                                            === x.value,
                                                    ) ?? ''
                                                }
                                                onChange={
                                                    this.onSelectChanged
                                                }
                                                required={
                                                    !!(
                                                        tenantSettings
                                                        ?? {}
                                                    ).billingEnabled
                                                    || !!(
                                                        organization
                                                        ?? {}
                                                    ).useInvoicing
                                                }
                                                error="Payment Terms are required for billing enabled organizations."
                                            />

                                            <Collapse isOpen={!organization.useContractSchedule}>
                                                <FormGroup
                                                    hidden={
                                                        !isCustomer
                                                        && !isDispatch
                                                        && !isPayroll
                                                    }
                                                >
                                                    <SubHeading className="mt-4">
                                                        Overtime Configuration
                                                    </SubHeading>
                                                </FormGroup>

                                                {Boolean(tenantSettings.enforceContractUnionWork) && (
                                                    <TipAlert
                                                        tipText="Note"
                                                        label="These settings only apply to contracts that do not have a union work type"
                                                        containerClassName="ml-5"
                                                    />
                                                )}

                                                <FormGroup
                                                    className="ml-5 mt-2"
                                                    style={{
                                                        marginBottom: '0px',
                                                    }}
                                                >
                                                    <FormLabel
                                                        htmlFor="invoiceTermsId"
                                                        label="Billing Overtime Range"
                                                    />

                                                    <Row className="g-0">
                                                        <Col>
                                                            <FormGroup
                                                                className="ml-1 mt-1"
                                                                style={{
                                                                    width: '80px',
                                                                }}
                                                            >
                                                                <TimePicker
                                                                    name="billingOTShiftStart"
                                                                    increment={0.25}
                                                                    value={organization.billingOTShiftStart}
                                                                    onChange={this.onTimeChanged}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col>
                                                            <FormGroup className="ml-1 mt-1">
                                                                <div
                                                                    style={{
                                                                        marginLeft:
                                                                            '12px',
                                                                        marginTop:
                                                                            '5px',
                                                                        fontSize:
                                                                            '12px',
                                                                        color: '#606060',
                                                                    }}
                                                                >
                                                                    <b>to</b>
                                                                </div>
                                                            </FormGroup>
                                                        </Col>
                                                        <Col>
                                                            <FormGroup
                                                                className="ml-1 mt-1"
                                                                style={{
                                                                    width: '80px',
                                                                }}
                                                            >
                                                                <TimePicker
                                                                    name="billingOTShiftEnd"
                                                                    increment={0.25}
                                                                    value={organization.billingOTShiftEnd}
                                                                    onChange={this.onTimeChanged}
                                                                />
                                                            </FormGroup>
                                                        </Col>

                                                        <Col />
                                                        <Col />
                                                        <Col />
                                                        <Col />
                                                        <Col />
                                                        <Col />
                                                        <Col />
                                                    </Row>
                                                </FormGroup>
                                                <FormGroup
                                                    className="ml-5 mt-2"
                                                    style={{
                                                        marginBottom: '0px',
                                                    }}
                                                >
                                                    <FormLabel
                                                        htmlFor="invoiceTermsId"
                                                        label="Billing Overtime Job Start Range"
                                                    />

                                                    <Row className="g-0">
                                                        <Col>
                                                            <FormGroup
                                                                className="ml-1 mt-1"
                                                                style={{
                                                                    width: '80px',
                                                                }}
                                                            >
                                                                <TimePicker
                                                                    name="billingOTJobStart"
                                                                    increment={0.25}
                                                                    value={organization.billingOTJobStart}
                                                                    onChange={this.onTimeChanged}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col>
                                                            <FormGroup className="ml-1 mt-1">
                                                                <div
                                                                    style={{
                                                                        marginLeft:
                                                                            '12px',
                                                                        marginTop:
                                                                            '5px',
                                                                        fontSize:
                                                                            '12px',
                                                                        color: '#606060',
                                                                    }}
                                                                >
                                                                    <b>to</b>
                                                                </div>
                                                            </FormGroup>
                                                        </Col>
                                                        <Col>
                                                            <FormGroup
                                                                className="ml-1 mt-1"
                                                                style={{
                                                                    width: '80px',
                                                                }}
                                                            >
                                                                <TimePicker
                                                                    name="billingOTJobEnd"
                                                                    increment={0.25}
                                                                    value={organization.billingOTJobEnd}
                                                                    onChange={this.onTimeChanged}
                                                                />
                                                            </FormGroup>
                                                        </Col>

                                                        <Col />
                                                        <Col />
                                                        <Col />
                                                        <Col />
                                                        <Col />
                                                        <Col />
                                                        <Col />
                                                    </Row>
                                                </FormGroup>

                                                <FormGroup className="ml-5">
                                                    <FormLabel
                                                        htmlFor="billingOTDays"
                                                        label="Billing Overtime Days"
                                                    />

                                                    <table className="billing-ot-days">
                                                        <tr>
                                                            <td
                                                                style={{
                                                                    paddingRight:
                                                                        '15px',
                                                                }}
                                                            >
                                                                <FormCheckbox
                                                                    small
                                                                    id="sundayOT"
                                                                    disabled={tenantSettings.quirksEnabled}
                                                                    checked={
                                                                        organization.billingOTDays
                                                                        & BillingOTDays.Sunday
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        this.onOTDaysChanged(
                                                                            e,
                                                                            BillingOTDays.Sunday,
                                                                        );
                                                                    }}
                                                                    labelText="Sunday"
                                                                />

                                                            </td>
                                                            <td
                                                                style={{
                                                                    paddingRight:
                                                                        '15px',
                                                                }}
                                                            >
                                                                <FormCheckbox
                                                                    small
                                                                    id="mondayOT"
                                                                    checked={
                                                                        organization.billingOTDays
                                                                        & BillingOTDays.Monday
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        this.onOTDaysChanged(
                                                                            e,
                                                                            BillingOTDays.Monday,
                                                                        );
                                                                    }}
                                                                    labelText="Monday"
                                                                />
                                                            </td>
                                                            <td
                                                                style={{
                                                                    paddingRight:
                                                                        '15px',
                                                                }}
                                                            >
                                                                <FormCheckbox
                                                                    small
                                                                    id="tuesdayOT"
                                                                    checked={
                                                                        organization.billingOTDays
                                                                        & BillingOTDays.Tuesday
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        this.onOTDaysChanged(
                                                                            e,
                                                                            BillingOTDays.Tuesday,
                                                                        );
                                                                    }}
                                                                    labelText="Tuesday"
                                                                />
                                                            </td>
                                                            <td
                                                                style={{
                                                                    paddingRight:
                                                                        '15px',
                                                                }}
                                                            >
                                                                <FormCheckbox
                                                                    small
                                                                    id="wednesdayOT"
                                                                    checked={
                                                                        organization.billingOTDays
                                                                        & BillingOTDays.Wednesday
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        this.onOTDaysChanged(
                                                                            e,
                                                                            BillingOTDays.Wednesday,
                                                                        );
                                                                    }}
                                                                    labelText="Wednesday"
                                                                />
                                                            </td>
                                                            <td
                                                                style={{
                                                                    paddingRight:
                                                                        '15px',
                                                                }}
                                                            >
                                                                <FormCheckbox
                                                                    small
                                                                    id="thursdayOT"
                                                                    checked={
                                                                        organization.billingOTDays
                                                                        & BillingOTDays.Thursday
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        this.onOTDaysChanged(
                                                                            e,
                                                                            BillingOTDays.Thursday,
                                                                        );
                                                                    }}
                                                                    labelText="Thursday"
                                                                />
                                                            </td>
                                                            <td
                                                                style={{
                                                                    paddingRight:
                                                                        '15px',
                                                                }}
                                                            >
                                                                <FormCheckbox
                                                                    small
                                                                    id="fridayOT"
                                                                    checked={
                                                                        organization.billingOTDays
                                                                        & BillingOTDays.Friday
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        this.onOTDaysChanged(
                                                                            e,
                                                                            BillingOTDays.Friday,
                                                                        );
                                                                    }}
                                                                    labelText="Friday"
                                                                />
                                                            </td>
                                                            <td>
                                                                <FormCheckbox
                                                                    small
                                                                    id="saturdayOT"
                                                                    checked={
                                                                        organization.billingOTDays
                                                                        & BillingOTDays.Saturday
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        this.onOTDaysChanged(
                                                                            e,
                                                                            BillingOTDays.Saturday,
                                                                        );
                                                                    }}
                                                                    labelText="Saturday"
                                                                />
                                                            </td>
                                                        </tr>
                                                    </table>
                                                </FormGroup>
                                            </Collapse>
                                        </>
                                    )}
                            </>
                        )}

                    {isCustomer
                        && organization.parentCustomerApply
                        && parentCustomers.length > 0 && (
                            <ValidatedSelect
                                id="types"
                                name="organization.parentCustomerId"
                                label="Parent Customer"
                                options={parentCustomers}
                                value={
                                    (parentCustomers ?? []).find(
                                        (x) => organization.parentCustomerId
                                            === x.value,
                                    ) ?? ''
                                }
                                onChange={this.onSelectChanged}
                            />
                        )}

                    <ValidatedSelect
                        id="types"
                        name="organization.parentCompanyId"
                        formGroupHidden={!parentRequired}
                        label={parentCompanyLabelText}
                        required={parentRequired}
                        options={parentCompanies}
                        value={
                            (parentCompanies ?? []).find(
                                (x) => organization.parentCompanyId
                                    === x.value,
                            ) ?? ''
                        }
                        onChange={this.onSelectChanged}
                        error="A billing organization is required."
                    />

                    {existing &&
                        (isCustomer || isParentCustomer || isProspect || isDispatch) && (
                            <>
                                <SubHeading className="mt-4">
                                    Contacts
                                </SubHeading>

                                {organization.companyStatusId == 1 && (
                                    <SimpleTable
                                        permission="company"
                                        onAddFunction={this.onAddContact}
                                        addLabelText="Add Contact"
                                        tableHeaderLabels={[
                                            { name: 'Name' },
                                            { name: 'Title' },
                                            { name: 'Email' },
                                            { name: 'Phone', width: '100px' },
                                            { name: 'Type' },
                                            { name: 'Is Active?' },
                                        ]}
                                        editable
                                        entities={organization.contacts}
                                        rowRenderer={this.renderContacts}
                                        noDataText="No contacts found."
                                    />
                                )}

                                {organization.companyStatusId != 1 && (
                                    <SimpleTable
                                        permission="company"
                                        tableHeaderLabels={[
                                            { name: 'Name' },
                                            { name: 'Title' },
                                            { name: 'Email' },
                                            { name: 'Phone', width: '100px' },
                                            { name: 'Type' },
                                            { name: 'Is Active?' },
                                        ]}
                                        editable={false}
                                        entities={organization.contacts}
                                        rowRenderer={this.renderContacts}
                                        noDataText="No contacts found."
                                    />
                                )}
                            </>
                        )}

                    {!!isCustomer && !!existing && (
                        <>
                            <SubHeading className="mt-4">
                                Jobs
                            </SubHeading>
                            <small
                                className="mt-2 mb-2"
                                hidden={
                                    !((organization ?? {}).jobs ?? [])
                                        .length
                                }
                            >
                                Displaying the 5 most recent jobs.
                            </small>
                            <SimpleTable
                                permission="company"
                                tableHeaderLabels={[
                                    { name: 'Job #' },
                                    { name: 'Job Name' },
                                    { name: 'Active?', width: '80px' },
                                ]}
                                editable
                                entities={this.state.organization.jobs}
                                rowRenderer={this.renderJobRows}
                                noDataText="No jobs found."
                            />

                            {!!(tenantSettings ?? {}).billingEnabled && isBillingRatesAllowed && (
                                <>
                                    <SubHeading className="mt-3">
                                        Charge Type Billing Rates
                                    </SubHeading>

                                    <BillingRateGrid
                                        showChargeType
                                        rateType={BillingRateTypes.CHARGE_TYPE}
                                        rates={orderBy(
                                            organization.billingRates,
                                            (x) => [
                                                x.chargeTypeName,
                                                x.billingRate
                                                    .effectiveDate,
                                            ],
                                        )}
                                        showDoubleTime={!!organization.billingEnableDT}
                                        showShiftDifferential={!!organization.hasCustomShiftDifferential}
                                        onAddRate={this.onAddRate}
                                        onEditRate={this.onEditRate}
                                        onShowRateHistory={
                                            this.onShowRateHistory
                                        }
                                    />
                                </>
                            )}
                        </>
                    )}

                    {isInvoicingAllowed
                        && isChargeTypeGroupsAllowed
                        && (organization.types ?? []).filter(
                            (x) => x == OrganizationTypes.Payroll,
                        ).length > 0 && (
                            <>
                                {!!(tenantSettings ?? {}).useStateMinimum && (
                                    <>
                                        <SubHeading className="mt-3">
                                            State Minimum Wage
                                        </SubHeading>
                                        <MinimumWageRateGrid
                                            onAddMinWageRate={
                                                this.onAddMinWageRate
                                            }
                                            onEditMinWageRate={
                                                this.onEditMinWageRate
                                            }
                                            minWageRates={orderBy(
                                                this.state.organization
                                                    .companyMinimumWageRates,
                                                (x) => [x.startDate],
                                                'desc',
                                            )}
                                        />
                                    </>
                                )}
                            </>
                        )}

                    {Boolean(existing)
                        && Boolean(isCustomer)
                        && Boolean(tenantSettings.billingEnabled)
                        && isBillingRatesAllowed
                        && Boolean(tenantSettings.chargeTypeGroupsEnabled) && (
                            <>
                                <SubHeading className="mt-3">
                                    Charge Type Group Billing Rates
                                </SubHeading>
                                <BillingRateGrid
                                    showChargeType
                                    rateType={BillingRateTypes.CHARGE_TYPE_GROUP}
                                    overtimeEnabled={false}
                                    rates={orderBy(
                                        visibleGroupBillingRates,
                                        (x) => [
                                            x.chargeTypeGroupName,
                                            x.billingRate
                                                .effectiveDate,
                                        ],
                                    )}
                                    showDoubleTime={false}
                                    onAddRate={this.onAddGroupRate}
                                    onEditRate={this.onEditGroupRate}
                                    onShowRateHistory={
                                        this.onShowRateHistory
                                    }
                                />
                            </>
                        )}

                    {!!isSubcontractor && !!existing && (
                        <FlexColumnCenter className="mb-3">
                            <SubHeading className="mt-4">
                                Subcontractor For
                            </SubHeading>

                            <ValidatedSelect
                                id="subcontractorCustomers"
                                name="organization.subcontractorCustomers"
                                formGroupClassName="mt-3 subcontractor-form-group"
                                label="Customer(s)"
                                isMulti
                                options={subcontractorCustomersOptions}
                                required
                                value={selectedSubcontractorCustomers}
                                onChange={
                                    this.subcontractorCustomersChanged
                                }
                                error="A customer selection is required."
                            />
                        </FlexColumnCenter>
                    )}

                    <FlexCenterRow className="mb-3">
                        <SaveButton
                            saving={!!this.state.saving}
                            label={!existing ? 'Save New Organization' : 'Save'}
                        />
                    </FlexCenterRow>

                    <div ref={this.billingRatesRef}>&nbsp;</div>
                </AppPageForm>

                {Boolean(organization.id) && (
                    <CompanyHistoryForm
                        show={this.state.showCompanyHistory}
                        companyId={organization.id}
                        onClose={this.onCloseCompanyHistory}
                    />
                )}

                {isCustomer || isParentCustomer || isProspect || isDispatch ? (
                    <>
                        <OrganizationContactForm
                            ref={this.contactFormRef}
                            show={showContactsForm}
                            contact={selectedContact}
                            readOnly={contactFormIsReadOnly}
                            allowContactTypeEdit={allowContactTypeEdit}
                            allowAllContactTypes
                            onClose={() => {
                                this.setState({ showContactsForm: false });
                            }}
                            onChange={(e) => {
                                const { selectedContact } = this.state;
                                selectedContact[e.target.name] = e.target.value;
                                this.setState({
                                    selectedContact,
                                });
                            }}
                            onCheckedChanged={(e) => {
                                const { selectedContact } = this.state;
                                selectedContact[e.target.name] = e.target.checked;
                                this.setState({
                                    selectedContact,
                                });
                            }}
                            onContactTypeChanged={(items) => {
                                const { selectedContact } = this.state;

                                selectedContact.contactTypes = items?.map(
                                    (x) => x.value,
                                ) || [];
                                selectedContact.locationContactTypeName = items?.map((x) => x.label).join(', ') || '';
                                this.setState({
                                    newContact: { ...selectedContact },
                                });
                            }}
                            onAddPreferredPersonnel={this.onAddPreferredPersonnel}
                            onRemovePreferredPersonnel={this.onRemovePreferredPersonnel}
                            onSaveCallback={this.onOrganizationContactSaved}
                        />

                        {!!isBillingRatesAllowed && (
                            <>
                                <BillingRateForm
                                    ref={this.rateFormRef}
                                    id="billingRateForm"
                                    entity={this.state.selectedRate}
                                    entityName="selectedRate"
                                    show={this.state.showRateForm}
                                    onClose={this.onRateFormClosed}
                                    onSaveCallback={this.onRateSaved}
                                    onRemoveCallback={this.onRateRemoved}
                                    onChange={this.onChange}
                                    onSelectChange={this.onSelectChanged}
                                    chargeTypes={chargeTypes}
                                    chargeTypeGroups={chargeTypeGroups}
                                    showChargeType
                                    showRemove={showRateFormDelete}
                                    showShiftDifferential={!!organization.hasCustomShiftDifferential}
                                    showDoubleTime={!!organization.billingEnableDT}
                                />

                                <BillingRateHistoryForm
                                    ref={this.rateHistoryFormRef}
                                    id="billingRateHistoryForm"
                                    entity={this.state.selectedRateHistory}
                                    show={this.state.showRateHistoryForm}
                                    onClose={this.onRateHistoryFormClosed}
                                />
                            </>
                        )}
                    </>
                ) : (
                    ''
                )}

                {showAddHolidayForm && (
                    <CreateHolidayForm
                        show
                        companyId={this.getOrganizationId()}
                        isCompanyHoliday
                        onHolidayCreated={this.onHolidayCreated}
                        onClose={this.onCloseHolidayForm}
                    />
                )}

                <MinimumWageRateForm
                    ref={this.minWageRateFormRef}
                    id="minWageRateForm"
                    entity={this.state.selectedMinWageRate}
                    entityName="selectedMinWageRate"
                    show={this.state.showMinWageRateForm}
                    onClose={this.onMinWageRateFormClosed}
                    onSaveCallback={this.onMinWageRateSaved}
                    onRemoveCallback={this.onMinWageRateRemoved}
                    onChange={this.onChange}
                    showRemove={showMinWageRateFormDelete}
                />
            </>
        );
    }
}

export default OrganizationForm;
